import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { NotificationBell } from '@/components/NotificationBell';
import { PendingRequestsNav } from '@/components/layout/PendingRequestsNav';
import { NavbarCredit } from '@/components/layout/NavbarCredit';
import { BangkokClock } from '@/components/layout/BangkokClock';
import { usePageTitle } from '@/components/layout/PageHeader';
import { BrandLogo } from '@/components/layout/BrandLogo';
import { useBoBrand } from '@/hooks/useBoBrand';
import { useAuthStore } from '@/stores/auth-store';

export function TopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const title = usePageTitle();
  const { name } = useBoBrand();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const initials = (user?.displayName ?? user?.username ?? '?')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b bg-background px-4 md:px-6">
      <div className="flex min-w-0 items-center gap-2">
        <BrandLogo className="md:hidden" />
        <h1 className="truncate text-lg font-semibold leading-none tracking-tight">
          {title ?? name ?? t('app.name')}
        </h1>
      </div>
      <div className="flex items-center gap-1">
        <BangkokClock />
        <NavbarCredit />
        <PendingRequestsNav />
        <NotificationBell />
        <ThemeSwitcher />
        <LanguageSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('user.account')}>
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{user?.displayName ?? user?.username ?? '—'}</span>
                {user?.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/account?tab=profile')}>
              <UserIcon className="mr-2 h-4 w-4" />
              {t('user.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/account?tab=security')}>
              <Settings className="mr-2 h-4 w-4" />
              {t('user.account')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void handleSignOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
